export const formatPhoneNumber = (phoneNumber: string) => {
  try {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumber;
  } catch (err) {
    return phoneNumber;
  }
}

export const getRelativeDate = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
  
    // Less than 1 minute ago
    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    }
    
    // Less than 1 hour ago
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    }
    
    // Less than 24 hours ago
    if (diffInHours < 24) {
      return `${diffInHours}h`;
    }
    
    // Less than 7 days ago
    if (diffInDays < 7) {
      return `${diffInDays}d`;
    }
    
    // Same year
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit'
      });
    }
    
    // Different year
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

export function formatNumberWithSpaces(number: number) {
  const locale = navigator.language || 'en-US'; // Fallback to 'en-US' if the locale is not available
  return new Intl.NumberFormat(locale, {
    useGrouping: true,
  }).format(number);
}

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const PI = Math.PI;
  const equatorialRadius = 6378137.0;
  const polarRadius = 6356752.314247833;
  const flattening = 1.0 / 298.257223563;

  // Helper function to convert degrees to radians
  const toRadians = (degrees: number) => (degrees / 180.0) * PI;

    const l = toRadians(lon2 - lon1);
    const u1 = Math.atan((1 - flattening) * Math.tan(toRadians(lat1)));
    const u2 = Math.atan((1 - flattening) * Math.tan(toRadians(lat2)));
    const sinU1 = Math.sin(u1);
    const cosU1 = Math.cos(u1);
    const sinU2 = Math.sin(u2);
    const cosU2 = Math.cos(u2);

    let cosSqAlpha;
    let sinSigma;
    let cos2SigmaM;
    let cosSigma;
    let sigma;

    let lambda = l;
    let lambdaP;
    let iterLimit = 100;

    do {
      const sinLambda = Math.sin(lambda);
      const cosLambda = Math.cos(lambda);
      sinSigma = Math.sqrt(
        Math.pow(cosU2 * sinLambda, 2) +
        Math.pow(cosU1 * sinU2 - sinU1 * cosU2 * cosLambda, 2)
      );

      if (sinSigma === 0.0) return 0.0; // Points are coincident

      cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
      sigma = Math.atan2(sinSigma, cosSigma);

      const sinAlpha = (cosU1 * cosU2 * sinLambda) / sinSigma;
      cosSqAlpha = 1 - Math.pow(sinAlpha, 2);
      cos2SigmaM = cosSigma - (2.0 * sinU1 * sinU2) / cosSqAlpha;

      const c = (flattening / 16) * cosSqAlpha * (4 + flattening * (4 - 3 * cosSqAlpha));
      lambdaP = lambda;
      lambda =
        l +
        (1 - c) *
        flattening *
        sinAlpha *
        (sigma +
          c *
          sinSigma *
          (cos2SigmaM + c * cosSigma * (-1 + 2.0 * Math.pow(cos2SigmaM, 2))));
    } while (Math.abs(lambda - lambdaP) > 1e-12 && --iterLimit > 0);

    if (iterLimit === 0) return 0.0; // Iteration limit exceeded

    const uSq =
      (cosSqAlpha * (Math.pow(equatorialRadius, 2) - Math.pow(polarRadius, 2))) /
      Math.pow(polarRadius, 2);
    const a =
      1 +
      (uSq / 16384) *
      (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
    const b =
      (uSq / 1024) *
      (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

    const deltaSigma =
      b *
      sinSigma *
      (cos2SigmaM +
        (b / 4) *
        (cosSigma * (-1 + 2.0 * Math.pow(cos2SigmaM, 2)) -
          (b / 6) *
          cos2SigmaM *
          (-3 + 4.0 * Math.pow(sinSigma, 2)) *
          (-3 + 4.0 * Math.pow(cos2SigmaM, 2))));

    return polarRadius * a * (sigma - deltaSigma);
};

export enum MeasurementSystem {
  METRIC = 'METRIC',
  IMPERIAL = 'IMPERIAL'
}

export function getMeasurementSystem() : MeasurementSystem {
    const userLanguage = navigator.language;
    return userLanguage !== "en-US" ? MeasurementSystem.METRIC : MeasurementSystem.IMPERIAL;
}

export function getLibMeasurementSystem() : string {
    return getMeasurementSystem() === MeasurementSystem.METRIC ? 'km' : 'miles';
}

export function approximateDistance(value: number, measurementSystem: MeasurementSystem = MeasurementSystem.METRIC, round = true) {
  if (measurementSystem === MeasurementSystem.METRIC) {
    if (round) {
      return Math.max(5.0, Math.ceil((value / 1000.0) / 5.0) * 5.0);
    } else {
      return value / 1000.0;
    }
  } else {
    if (round) {
      return Math.max(5.0, Math.ceil((value / 1609.34) / 5.0) * 5.0);
    } else {
      return value / 1609.34;
    }
  }
}

export function dateStringToLocaleHour(date: string) {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function dateStringToLocaleDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export function dateStringToLocaleDateWithoutYear(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
  });
}

export const localeFormatNumber = 'en-US';

export function formatAmount(amount: number, floor: boolean = false) {
  if(floor)
    amount = Math.floor(amount)

  return amount.toLocaleString(localeFormatNumber);
}

export function formatAmountWithSATS(amount: number, floor: boolean = false) {
  if(floor)
    amount = Math.floor(amount)

  return `${formatAmount(amount)} SAT${amount > 1 ? 'S' : ''}`;
}

export function formatNumberWithCommas(value: string): string {
  // Remove any non-digits
  const numbers = value.replace(/[^\d]/g, '');
  // Convert to number and format with commas
  return numbers === '' ? '' : Number(numbers).toLocaleString(localeFormatNumber);
};

export function getFlagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0))) // Use `charCodeAt(0)`
    .join('');
}